import { useMemo } from 'react';

import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { useGetAllAtsConnectionsQuery } from 'shared/legacy/hooks/useGetAllAtsConnectionsQuery';
import { useCampaignStore } from 'stores/campaign/campaign.store';
import { useRobinJrSelectedVacancyOptional } from 'stores/robin-jr/robin-jr.store';
import { ALLOWED_ATS_CODE_NAMES } from '@/views/LoggedIn/components/ATSIntegrations/components/ATSIntegrationForm/hooks/useGetStatuses';
import { OutreachType } from 'types/outreach';
import { SelectAtsVacancyModalType } from './SelectAtsVacancyForCampaignModal.types';

const ALLOWED_CHANNELS_FOR_ATS_PROMPT = [
  OutreachType.Indeed,
  OutreachType.Email,
  OutreachType.Linkedin,
  OutreachType.LinkedinInmail,
];

export const useShouldShowAtsVacancyModal = (options: {
  variant: SelectAtsVacancyModalType;
  channel?: OutreachType;
}) => {
  const { variant, channel } = options;
  const { data: atsConnections } = useGetAllAtsConnectionsQuery();
  const campaign = useCampaignStore((s) => s.campaign);
  const selectedVacancy = useRobinJrSelectedVacancyOptional();
  const pageContext = usePageContext();

  return useMemo(() => {
    const campaignHasAts = !!campaign?.connected_ats;
    const atsAllowed = ALLOWED_ATS_CODE_NAMES.includes(atsConnections?.activeATS?.ats_name?.toLowerCase() || '');

    const allowedPages = [PageContext.Campaign, PageContext.Shared];
    const isAllowedPage = allowedPages.includes(pageContext);

    const channelOk =
      variant === SelectAtsVacancyModalType.Scheduling ||
      variant === SelectAtsVacancyModalType.Matching ||
      (channel !== undefined && ALLOWED_CHANNELS_FOR_ATS_PROMPT.includes(channel));

    const notContactingOrNoAtsVacancySelected =
      variant !== SelectAtsVacancyModalType.Contacting || !selectedVacancy?.job_ats_id;

    return (
      !!atsConnections?.activeATS &&
      channelOk &&
      !campaignHasAts &&
      isAllowedPage &&
      notContactingOrNoAtsVacancySelected &&
      atsAllowed
    );
  }, [atsConnections?.activeATS, campaign?.connected_ats, channel, pageContext, selectedVacancy?.job_ats_id, variant]);
};
